<template>
  <div class="fund-amount">
    <template v-if="swapCurrency && fundOptions.includes('Wallet')">
      <form @submit.prevent="fundAmount" class="fund-amount__form">
        <div class="fund-amount__header">
          <h2 class="fund-amount__title">Enter amount to convert</h2>
          <p class="fund-amount__subtitle" v-if="currencyRate">
            {{ currency }} equivalent: {{ formatAmountToDollar(swapConversion, 2, currency) }}
          </p>
        </div>
        
        <div class="fund-amount__input-group">
          <label class="fund-amount__label">Amount ({{ swapCurrency }})</label>
          <div class="fund-amount__input-wrapper">
            <span class="fund-amount__currency-symbol">{{ getCurrencySymbol(swapCurrency) }}</span>
            <input
              type="text"
              name="amount"
              :disabled="loading"
              id="amount"
              class="fund-amount__input"
              :value="displayAmount"
              @input="handleAmountInput"
              placeholder="0.00"
            />
          </div>
          <p class="fund-amount__balance">Available: {{ formatAmountToDollar(currencyBalance || 0, 2, swapCurrency) }}</p>
        </div>

        <div class="fund-amount__rate-card" v-if="currencyRate && !loading">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1.33334C4.32 1.33334 1.33334 4.32 1.33334 8C1.33334 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8C14.6667 4.32 11.68 1.33334 8 1.33334ZM8.66667 11.3333H7.33334V7.33334H8.66667V11.3333ZM8.66667 6H7.33334V4.66667H8.66667V6Z" fill="#4D84FF"/>
          </svg>
          <span>Current FX rate: <strong>{{ formatAmountToDollar(1, 2, swapCurrency) }} = {{ formatAmountToDollar(currencyRate, 4, currency) }}</strong></span>
        </div>

        <div class="fund-amount__actions">
          <Btn
            :loading="loading"
            title="Continue"
            :disabled="loading || !fundPayload.amount"
          />
        </div>
      </form>
    </template>
    <template v-else>
      <div class="fund-amount__wallet-info">
        <div class="fund-amount__wallet-card">
          <span class="fund-amount__wallet-label">Funding to</span>
          <div class="fund-amount__wallet-details">
            <CurrencyFlag :currency="currency" />
            <span class="fund-amount__wallet-name">{{ currency }} Wallet</span>
          </div>
        </div>
      </div>
      
      <form @submit.prevent="fundAmount" class="fund-amount__form">
        <div class="fund-amount__input-group">
          <label class="fund-amount__label">Enter Amount ({{ currency }})</label>
          <div class="fund-amount__input-wrapper">
            <span class="fund-amount__currency-symbol">{{ getCurrencySymbol(currency) }}</span>
            <input
              type="text"
              name="amount"
              id="amount"
              class="fund-amount__input"
              :value="displayAmount"
              @input="handleAmountInput"
              placeholder="0.00"
            />
          </div>
          <p class="fund-amount__minimum" v-if="minimumDeposit">Minimum: {{ formatAmountToDollar(minimumDeposit, 2, currency) }}</p>
        </div>

        <div class="fund-amount__actions">
          <Btn
            :loading="loading"
            title="Continue"
            :disabled="loading || !fundPayload.amount"
          />
        </div>
      </form>
    </template>
  </div>
</template>

<script>
import Btn from "@/components/general/BtnComponent.vue";
import NumberInput from "@/components/wallet/NumberInput.vue";
import { formatAmountToDollar, convertToLocale, getCurrencySymbol } from "@/utils/helpers.js";
import fundPercent from "@/utils/js/fundPercent.js";
import { getItem, setItem } from "@/utils/storage.js";
import { mapActions, mapGetters, mapState } from "vuex";
import CurrencyFlag from "../CurrencyFlag.vue";

export default {
  name: "FundAmountForm",

  emits: ["storeData"],

  components: {
    NumberInput,
    Btn,
    CurrencyFlag,
  },

  props: {
    fundOptions: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      amountValid: false,
      validInput: {
        amount: false,
        dollarEquivalent: false,
      },
      fundPercent,
      fundPayload: { amount: "" },
      dollarEquivalent: 0,
      minAmount: 0,
      transactionFee: 0,
      loading: false,
      displayAmount: "",
    };
  },
  inject: ["swapCurrency"],

  computed: {
    ...mapState({
      fundMethod: (state) => state.organisationModule.fundMethod,
      currency: (state) => state.organisationModule.currency,
      exchangeRate: (state) => state.organisationModule.exchangeRate,
    }),
    ...mapGetters("organisationModule", [
      "minimumDeposit",
      "currencyRate",
      "walletBalance",
    ]),
    swapConversion() {
      if (this.swapCurrency) {
        return this.fundPayload.amount * Number(this.currencyRate);
      }
      return 0;
    },
    currencyBalance() {
      if (this.swapCurrency) {
        return this.walletBalance.find(
          (wallet) => wallet.currency === this.swapCurrency
        )?.balance;
      }
      return null;
    },

    ...mapGetters("fundingModule", [
      "currentFundPayload",
      "transactionFee",
      "totalAmount",
    ]),

    currentTransactionFee() {
      return this.transactionFee(
        this.fundOptions,
        this.fundPayload.amount,
        this.currency
      );
    },
  },
  mounted() {
    this.getMinimumFunding();
    if (this.fundOptions.includes("Wallet")) {
      this.getRate();
    }
    const details = getItem("fundPayload");
    const amount = details?.amount || 0;
    this.fundPayload.amount = amount;
    if (amount) {
      this.displayAmount = convertToLocale(amount);
    }
  },

  watch: {
    "fundPayload.amount"(newAmount) {
      this.updateFundingDetails({
        fundOptions: this.fundOptions,
        amount: newAmount,
        currency: this.currency,
      });
    },
  },

  methods: {
    ...mapActions("organisationModule", [
      "getMinimumFunding",
      "convertCurrency",
    ]),
    ...mapActions("fundingModule", ["updateFundingDetails"]),
    formatAmountToDollar,
    convertToLocale,
    getCurrencySymbol,
    
    handleAmountInput(e) {
      const value = e.target.value.replace(/,/g, "");
      if (!isNaN(value) && value !== "") {
        this.fundPayload.amount = parseFloat(value);
        this.displayAmount = convertToLocale(value);
      } else if (value === "") {
        this.fundPayload.amount = "";
        this.displayAmount = "";
      }
    },
    
    async getRate() {
      const exchangeQuery = {
        from: this.swapCurrency,
        to: this.currency,
        amount: this.fundPayload.amount || 1,
      };
      this.loading = true;
      await this.convertCurrency(exchangeQuery);
      this.loading = false;
    },

    fundAmount() {
      switch (this.fundOptions) {
        case "Mobile Money":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "Fund with bank transfer":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "Fund with debit card":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "P2P Transfers":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "":
          this.$emit("storeData", "fundProvider");
          break;
        case "Fund with Fintech Apps":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "KES Wallet":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "USD Wallet":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "NGN Wallet":
          this.$emit("storeData", "fundAmountForm");
          break;
        case "Crypto":
          this.$emit("storeData", "fundAmountForm");
          break;

        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fund-amount {
  padding: 8px 24px;

  &__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__header {
    margin-bottom: 8px;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__wallet-info {
    margin-bottom: 24px;
  }

  &__wallet-card {
    padding: 16px 20px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
  }

  &__wallet-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #64748B;
    display: block;
    margin-bottom: 8px;
  }

  &__wallet-details {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__wallet-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
  }

  &__input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__currency-symbol {
    position: absolute;
    left: 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #64748B;
  }

  &__input {
    width: 100%;
    height: 52px;
    padding: 0 16px 0 40px;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    background: white;
    transition: border-color 0.15s ease;

    &:focus {
      outline: none;
      border-color: #4D84FF;
    }

    &::placeholder {
      color: #94A3B8;
    }
  }

  &__balance,
  &__minimum {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__rate-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: #EFF6FF;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;

    strong {
      font-weight: 500;
      color: #0F172A;
    }

    svg {
      flex-shrink: 0;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;

    :deep(.btn) {
      min-width: 140px;
      height: 44px;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
    }
  }
}
</style>
